import { create } from 'zustand';
import type { POI, Tag, ChangelogEvent, ChangelogAction, EntityType } from '@poi-planner/shared';
import { api } from '../services/api';
import { useUserStore } from './userStore';
import { useAuthStore } from './authStore';
import { AUTO_SAVE_DELAY_MS } from '@poi-planner/shared';

export type SaveStatus = 'saved' | 'saving' | 'error' | 'unsaved';

interface LibraryState {
  // Data
  pois: POI[];
  tags: Tag[];

  // UI state
  loading: boolean;
  error: string | null;
  saveStatus: SaveStatus;

  // Changelog (for library-level changes)
  pendingChangelog: ChangelogEvent[];

  // Actions
  loadLibrary: () => Promise<void>;
  setLibraryData: (pois: POI[], tags?: Tag[]) => void;

  // POI CRUD
  createPOI: (poi: Omit<POI, 'id'>) => string;
  updatePOI: (id: string, updates: Partial<POI>) => void;
  deletePOI: (id: string) => void;
  getPOI: (id: string) => POI | undefined;

  // Tag CRUD
  createTag: (tag: Omit<Tag, 'id'>) => Promise<string>;
  updateTag: (id: string, updates: Partial<Omit<Tag, 'id'>>) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;
  getTag: (id: string) => Tag | undefined;

  // Accommodation helpers (POIs with "accommodation" tag)
  getAccommodationPOIs: () => POI[];
  getAccommodation: (id: string) => POI | undefined;
}

// Auto-save debounce state
let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let pendingFlush: Promise<void> | null = null;

async function doSave(): Promise<void> {
  const { pois, pendingChangelog } = useLibraryStore.getState();

  useLibraryStore.setState({ saveStatus: 'saving' });
  try {
    // Save POIs
    await api.savePOIs(pois);

    // Save changelog if there are pending entries
    if (pendingChangelog.length > 0) {
      await api.saveLibraryChangelog(pendingChangelog);
      useLibraryStore.setState({ pendingChangelog: [] });
    }

    useLibraryStore.setState({ saveStatus: 'saved' });
  } catch {
    useLibraryStore.setState({ saveStatus: 'error' });
  }
}

function queueSave() {
  if (saveTimeout) clearTimeout(saveTimeout);
  useLibraryStore.setState({ saveStatus: 'unsaved' });

  saveTimeout = setTimeout(() => {
    saveTimeout = null;
    pendingFlush = doSave().finally(() => {
      pendingFlush = null;
    });
  }, AUTO_SAVE_DELAY_MS);
}

// Changelog helper
function addChangelog(
  action: ChangelogAction,
  entityType: EntityType,
  entityId: string,
  summary: string,
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
) {
  const user = useUserStore.getState().displayName || 'Unknown';
  const userId = useAuthStore.getState().user?.id ?? null;
  const event: ChangelogEvent = {
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    user,
    userId,
    action,
    entityType,
    entityId,
    summary,
    before,
    after,
    undoOf: null,
  };
  const pending = useLibraryStore.getState().pendingChangelog;
  useLibraryStore.setState({ pendingChangelog: [...pending, event] });
}

export const useLibraryStore = create<LibraryState>((set, get) => ({
  pois: [],
  tags: [],
  loading: true,
  error: null,
  saveStatus: 'saved' as SaveStatus,
  pendingChangelog: [],

  loadLibrary: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.loadLibrary();
      set({
        pois: data.pois,
        tags: data.tags ?? [],
        loading: false,
      });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  setLibraryData: (pois: POI[], tags?: Tag[]) => {
    if (tags !== undefined) {
      set({ pois, tags });
    } else {
      set({ pois });
    }
  },

  // --- POI CRUD ---

  createPOI: (poiData: Omit<POI, 'id'>) => {
    const id = crypto.randomUUID();
    const poi: POI = { ...poiData, id };
    const { pois } = get();
    set({ pois: [...pois, poi] });
    addChangelog('create', 'poi', id, `Created "${poi.name}"`, null, { name: poi.name });
    queueSave();
    return id;
  },

  updatePOI: (id: string, updates: Partial<POI>) => {
    const { pois } = get();
    const idx = pois.findIndex((p) => p.id === id);
    if (idx === -1) return;
    const before = pois[idx];
    const updated = { ...before, ...updates, id };
    const newPois = [...pois];
    newPois[idx] = updated;
    set({ pois: newPois });
    addChangelog('update', 'poi', id, `Updated "${updated.name}"`, { name: before.name }, { name: updated.name });
    queueSave();
  },

  deletePOI: (id: string) => {
    const { pois } = get();
    const poi = pois.find((p) => p.id === id);
    if (!poi) return;
    const newPois = pois.filter((p) => p.id !== id);
    set({ pois: newPois });
    addChangelog('delete', 'poi', id, `Deleted "${poi.name}"`, { name: poi.name }, null);
    queueSave();
  },

  getPOI: (id: string) => {
    return get().pois.find((p) => p.id === id);
  },

  // --- Tag CRUD ---

  createTag: async (tagData: Omit<Tag, 'id'>) => {
    const tag = await api.createTag(tagData);
    const { tags } = get();
    set({ tags: [...tags, tag] });
    return tag.id;
  },

  updateTag: async (id: string, updates: Partial<Omit<Tag, 'id'>>) => {
    const updatedTag = await api.updateTag(id, updates);
    const { tags } = get();
    const idx = tags.findIndex((t) => t.id === id);
    if (idx !== -1) {
      const newTags = [...tags];
      newTags[idx] = updatedTag;
      set({ tags: newTags });
    }
  },

  deleteTag: async (id: string) => {
    await api.deleteTag(id);
    const { tags, pois } = get();
    // Remove tag from list
    const newTags = tags.filter((t) => t.id !== id);
    // Clear primaryTagId from any POIs referencing this tag
    const newPois = pois.map((p) =>
      p.primaryTagId === id ? { ...p, primaryTagId: null } : p
    );
    set({ tags: newTags, pois: newPois });
    // Save POIs if any were updated
    if (pois.some((p) => p.primaryTagId === id)) {
      queueSave();
    }
  },

  getTag: (id: string) => {
    return get().tags.find((t) => t.id === id);
  },

  // --- Accommodation helpers ---
  // Accommodations are now POIs with the "accommodation" tag

  getAccommodationPOIs: () => {
    return get().pois.filter((p) =>
      p.tags?.some((t) => t.toLowerCase() === 'accommodation')
    );
  },

  getAccommodation: (id: string) => {
    const poi = get().pois.find((p) => p.id === id);
    if (!poi) return undefined;
    // Only return if it has accommodation tag
    if (poi.tags?.some((t) => t.toLowerCase() === 'accommodation')) {
      return poi;
    }
    return undefined;
  },
}));

// Export flush function for external use (e.g., before navigation)
export async function flushLibrarySave(): Promise<void> {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
    saveTimeout = null;
    await doSave();
  } else if (pendingFlush) {
    await pendingFlush;
  }
}
